import { ConnectionType, type CustomEdge } from '@/types/edge-types';
import {
  HandleType,
  NodeCategory,
  type CustomNode,
  type NodeStruct,
} from '@/types/node-types';
import {
  Table2,
  FileText,
  Presentation,
  Image,
  FileCode2,
  FileVideo,
  FileAudio,
  type LucideIcon,
} from 'lucide-react';
import {
  SPREADSHEET_EXTENSIONS,
  PRESENTATION_EXTENSIONS,
  PRESENTATION_EXTENSIONS_EXTRA,
  DOCUMENT_EXTENSIONS,
  DOCUMENT_EXTENSIONS_EXTRA,
  TEXT_EXTENSIONS,
  IMAGE_EXTENSIONS,
  VIDEO_EXTENSIONS,
  AUDIO_EXTENSIONS,
} from '@/types/source-types';
import {
  getConditionalBranchLabel,
  getSelectedCase,
  isConditionalEdge,
} from '@/pages/workflow/helper/functional-control-flow';

// Simplified node styling - only TOOL and MEMORY have unique colors
// Node styling interface
export interface NodeStyling {
  label: string;
  icon: string;
  primaryColor: string;
  secondaryColor: string;
  tertiaryColor?: string;
  accentColor: string;
}

// Simplified node styling - only TOOL and MEMORY have unique colors
export const NODE_STYLING: Record<string, NodeStyling> = {
  [NodeCategory.TOOL]: {
    label: 'Tools',
    icon: 'WrenchIcon',
    primaryColor: '#A855F7', // purple
    secondaryColor: '#F3E8FF',
    tertiaryColor: '#FAF5FF',
    accentColor: '#3B0764',
  },
  [NodeCategory.MEMORY]: {
    label: 'Memory',
    icon: 'BrainIcon',
    primaryColor: '#84CC16', // lime
    secondaryColor: '#ECFCCB',
    tertiaryColor: '#F7FEE7',
    accentColor: '#365314',
  },
};

// Default styling for all categories except TOOL and MEMORY (uses Capitol blue)
export const DEFAULT_NODE_STYLING: NodeStyling = {
  label: 'Default',
  icon: 'WorkflowIcon',
  primaryColor: '#1d76f3', // Capitol blue
  secondaryColor: '#DAEEFF',
  tertiaryColor: '#F0F9FF',
  accentColor: '#0369A1',
};

// Category-specific styling mapping (for icons and labels)
export const CATEGORY_METADATA = {
  [NodeCategory.INPUT]: { label: 'Inputs', icon: 'LogIn' },
  [NodeCategory.AGENT]: { label: 'Agents', icon: 'BotIcon' },
  [NodeCategory.TOOL]: { label: 'Tools', icon: 'WrenchIcon' },
  [NodeCategory.LOGIC]: { label: 'Logic', icon: 'Shapes' },
  [NodeCategory.OUTPUT]: { label: 'Outputs', icon: 'LogOut' },
  [NodeCategory.MEMORY]: { label: 'Memory', icon: 'BrainIcon' },
  [NodeCategory.GOVERNANCE]: { label: 'Governance', icon: 'SlidersHorizontal' },
  [NodeCategory.DATA]: { label: 'Data', icon: 'Database' },
  [NodeCategory.INTEGRATION]: { label: 'Integration', icon: 'Cable' },
} as const;

/**
 * Gets the styling for a given node category.
 * TOOL and MEMORY have unique colors, all others use Capitol blue.
 */
export const getNodeStyling = (category: NodeCategory): NodeStyling => {
  const baseStyling = NODE_STYLING[category as keyof typeof NODE_STYLING];
  if (baseStyling) {
    return baseStyling;
  }

  // For categories without custom styling, use default with category-specific icon and label
  const metadata =
    CATEGORY_METADATA[category as keyof typeof CATEGORY_METADATA];
  if (metadata) {
    return {
      ...DEFAULT_NODE_STYLING,
      label: metadata.label,
      icon: metadata.icon,
    };
  }

  return DEFAULT_NODE_STYLING;
};

// Capitol primary blue used for most node categories
export const CAPITOL_PRIMARY_BLUE = '#1d76f3';

// Grey color for disabled/locked/unconnected states
export const GREY_COLOR = '#64748B'; // grey-500

// Red color for validation errors
export const ERROR_RED = '#EF4444'; // red-500
export const CONDITIONAL_EDGE_COLOR = '#8B5CF6';
export const SELECTED_CONDITIONAL_EDGE_COLOR = '#F59E0B';
/** For-each "each item" and "collect" edges (teal-500: legible on light and dark canvases). */
export const FOR_EACH_EDGE_COLOR = '#14b8a6';

// Predefined color options for icon_color selection
export const COLOR_OPTIONS = [
  { value: 'default', label: 'Default', color: null },
  { value: '#1D76F3', label: 'Blue', color: '#1D76F3' },
  { value: '#A855F7', label: 'Purple', color: '#A855F7' },
  { value: '#84CC16', label: 'Lime', color: '#84CC16' },
  { value: '#EF4444', label: 'Red', color: '#EF4444' },
  { value: '#F59E0B', label: 'Orange', color: '#F59E0B' },
  { value: '#10B981', label: 'Green', color: '#10B981' },
  { value: '#EC4899', label: 'Pink', color: '#EC4899' },
  { value: '#8B5CF6', label: 'Violet', color: '#8B5CF6' },
  { value: '#06B6D4', label: 'Cyan', color: '#06B6D4' },
  { value: '#64748B', label: 'Slate', color: '#64748B' },
] as const;

/**
 * Determines the icon color for a node based on its category.
 * - Custom color takes priority if set
 * - TOOL nodes use purple (#A855F7)
 * - MEMORY nodes use lime (#84CC16)
 * - All other categories return null (theme-dependent: black in light, white in dark)
 */
export const getNodeIconColor = (
  nodeType: NodeCategory,
  customColor?: string,
): string | null => {
  // Custom color takes priority
  if (customColor) {
    return customColor;
  }

  // Category-specific colors for TOOL and MEMORY
  if (nodeType === NodeCategory.TOOL) {
    return NODE_STYLING[NodeCategory.TOOL].primaryColor; // #A855F7 purple
  }
  if (nodeType === NodeCategory.MEMORY) {
    return NODE_STYLING[NodeCategory.MEMORY].primaryColor; // #84CC16 lime
  }

  // Return null for theme-dependent colors (black in light, white in dark)
  return null;
};

export const HandleTypes = {
  PARAM: 'param-handle',
  /** Param input/output that accepts only `file` */
  PARAM_FILE: 'param-file-handle',
  TOOL: 'tool-handle',
  MEMORY: 'memory-handle',
};

export const HANDLE_STYLING = {
  [HandleTypes.PARAM]: {
    color: '#1D76F3',
  },
  [HandleTypes.PARAM_FILE]: {
    color: '#10B981',
  },
  [HandleTypes.TOOL]: {
    color: '#A855F7',
  },
  [HandleTypes.MEMORY]: {
    color: '#84CC16',
  },
} as const;

export type HandleVariant = 'param' | 'tool' | 'memory';

export type ParamHandleColorStyle = {
  color: string;
  backgroundColor?: string;
  background?: string;
};

/**
 * Fill for param handles from `accepted_types`: default blue, file-only green,
 * or a blue→green gradient when both file and other types are accepted.
 */
function getSourceOutputAcceptedTypes(
  sourceHandle: string | null | undefined,
  struct: NodeStruct | undefined,
): HandleType[] | null {
  if (!struct || !sourceHandle) return null;
  const port = struct.output_ports?.find((p) => p.id === sourceHandle);
  return port?.port_type?.accepted_types ?? null;
}

/**
 * Resolves accepted types for the target side of an edge: top-level input
 * port id takes precedence over a bindable param input port id, matching
 * other handle-resolution callsites.
 */
function getTargetPortAcceptedTypes(
  targetHandle: string | null | undefined,
  struct: NodeStruct | undefined,
): HandleType[] | null {
  if (!struct || !targetHandle) return null;
  const port = struct.input_ports?.find((p) => p.id === targetHandle);
  if (port) {
    return port.port_type?.accepted_types ?? null;
  }
  for (const param of struct.params ?? []) {
    const ip = param.bindable_config?.input_port;
    if (ip?.id === targetHandle) {
      return ip.port_type?.accepted_types ?? null;
    }
  }
  return null;
}

function portAcceptsFile(types: HandleType[] | null | undefined): boolean {
  return !!types?.includes(HandleType.FILE);
}

/**
 * Solid stroke color for port/param edges. When both ends accept `file`, matches
 * file-only handle green; otherwise follows source node category (tool / memory / default param blue).
 */
export function getWorkflowEdgeStrokeColor(
  edge: CustomEdge,
  sourceNode: CustomNode | undefined,
  targetNode: CustomNode | undefined,
): string {
  const defaultBlue = HANDLE_STYLING[HandleTypes.PARAM].color;

  if (isConditionalEdge(edge)) {
    const selectedCase = getSelectedCase(sourceNode?.data?.result);
    const branchLabel = getConditionalBranchLabel(edge);
    return selectedCase && selectedCase === branchLabel
      ? SELECTED_CONDITIONAL_EDGE_COLOR
      : CONDITIONAL_EDGE_COLOR;
  }

  if ((edge.data as { isArtifact?: boolean } | undefined)?.isArtifact) {
    return HANDLE_STYLING[HandleTypes.PARAM_FILE].color;
  }
  const repeatRole = (edge.data as { repeat_role?: string } | undefined)
    ?.repeat_role;
  if (
    edge.edge_type === ConnectionType.REPEAT ||
    repeatRole === 'repeat_feedback'
  ) {
    return '#d97706';
  }
  if (repeatRole === 'repeat_condition') {
    return '#7c3aed';
  }
  // --- for each (Phase 8c): both body edges are teal (reads on both themes) ---
  if (
    edge.edge_type === ConnectionType.MAP_COLLECT ||
    (edge.data as { map_role?: string } | undefined)?.map_role ===
      'map_collect' ||
    (edge.data as { map_role?: string } | undefined)?.map_role === 'map_item' ||
    edge.sourceHandle === 'map:item'
  ) {
    return FOR_EACH_EDGE_COLOR;
  }
  // --- repeat loop (Phase 3): a judge edge drawn by hand has no role marker;
  // it is the data edge into a Repeat Until node. ---
  if (
    targetNode?.data?.struct?.node_id === 'repeat_until_node' &&
    (edge.edge_type === ConnectionType.PORT_TO_PARAM ||
      edge.edge_type === ConnectionType.PORT_TO_PORT)
  ) {
    return '#7c3aed';
  }
  // --- end repeat loop ---

  if (!sourceNode?.data?.struct || !targetNode?.data?.struct) {
    return defaultBlue;
  }
  const srcStruct = sourceNode.data.struct;
  const tgtStruct = targetNode.data.struct;

  const sourceTypes = getSourceOutputAcceptedTypes(
    edge.sourceHandle,
    srcStruct,
  );
  const targetTypes = getTargetPortAcceptedTypes(edge.targetHandle, tgtStruct);

  if (portAcceptsFile(sourceTypes) && portAcceptsFile(targetTypes)) {
    return HANDLE_STYLING[HandleTypes.PARAM_FILE].color;
  }

  const nodeType = srcStruct.node_type as NodeCategory;
  switch (nodeType) {
    case NodeCategory.TOOL:
      return HANDLE_STYLING[HandleTypes.TOOL].color;
    case NodeCategory.MEMORY:
      return HANDLE_STYLING[HandleTypes.MEMORY].color;
    default:
      return defaultBlue;
  }
}

export function getParamHandleColor(
  acceptedTypes: readonly HandleType[],
): ParamHandleColorStyle {
  const param = HANDLE_STYLING[HandleTypes.PARAM];
  const file = HANDLE_STYLING[HandleTypes.PARAM_FILE];
  const unique = new Set(acceptedTypes);

  if (!unique.has(HandleType.FILE)) {
    return { backgroundColor: param.color, color: param.color };
  }

  const acceptsOnlyFile = [...unique].every((t) => t === HandleType.FILE);
  if (acceptsOnlyFile) {
    return { backgroundColor: file.color, color: file.color };
  }

  return {
    background: `linear-gradient(to bottom, ${param.color}, ${file.color})`,
    color: param.color,
  };
}

// Get category-specific styling and configuration for connection fields
export const getCategoryConfig = (
  category: string,
  port_id: string,
  display_name: string,
  accepted_types?: string[],
) => {
  switch (category) {
    case NodeCategory.TOOL:
      return {
        title: 'Tools',
        addText: 'Add Tool',
        handleId: port_id,
        handleClass: 'group-hover:!border-purple-500',
        icon: NODE_STYLING[NodeCategory.TOOL].icon,
        styling: NODE_STYLING[NodeCategory.TOOL],
        handleStyling: HANDLE_STYLING[HandleTypes.TOOL],
      };
    case NodeCategory.MEMORY:
      return {
        title: 'Memory',
        addText: 'Add Memory',
        handleId: port_id,
        handleClass: 'group-hover:!border-lime-500',
        icon: NODE_STYLING[NodeCategory.MEMORY].icon,
        styling: NODE_STYLING[NodeCategory.MEMORY],
        handleStyling: HANDLE_STYLING[HandleTypes.MEMORY],
      };
    case NodeCategory.INPUT:
      return {
        title: display_name,
        addText: `Add ${display_name}`,
        handleId: port_id,
        handleClass: 'group-hover:!border-[#1D76F3]',
        icon: CATEGORY_METADATA[NodeCategory.INPUT].icon,
        styling: getNodeStyling(NodeCategory.INPUT),
        handleStyling: getParamHandleColor(
          accepted_types?.map((type) => type as HandleType) || [],
        ),
      };
    case NodeCategory.OUTPUT:
      return {
        title: display_name,
        addText: `Add Output`,
        handleId: port_id,
        handleClass: 'group-hover:!border-[#1D76F3]',
        icon: CATEGORY_METADATA[NodeCategory.OUTPUT].icon,
        styling: getNodeStyling(NodeCategory.OUTPUT),
        handleStyling: getParamHandleColor(
          accepted_types?.map((type) => type as HandleType) || [],
        ),
      };
    default:
      return {
        title: display_name,
        addText: `Add default`,
        handleId: port_id,
        handleClass: 'group-hover:!border-[#1D76F3]',
        icon: 'TextCursorInputIcon',
        styling: DEFAULT_NODE_STYLING,
        handleStyling: {
          color: '#1D76F3',
        },
      };
  }
};

// Tailwind color values for file type backgrounds
export const FILE_TYPE_COLORS = {
  spreadsheet: '#22c55e', // green-500
  presentation: '#f97316', // orange-500
  document: '#f43f5e', // rose-500
  image: '#eab308', // yellow-500
  text: '#3b82f6', // blue-500
  video: '#8b5cf6', // violet-500
  audio: '#ec4899', // pink-500
  default: '#64748b', // slate-500
} as const;

/**
 * Gets the file icon component based on file extension.
 * @param filename - The filename with extension
 * @returns The Lucide icon component for the file type
 */
export const getFileIcon = (filename: string): LucideIcon => {
  const extension = filename.split('.').pop()?.toLowerCase() || '';

  // Spreadsheet files
  if (SPREADSHEET_EXTENSIONS.includes(extension)) {
    return Table2;
  }

  // Word/text files
  if (
    DOCUMENT_EXTENSIONS.includes(extension) ||
    DOCUMENT_EXTENSIONS_EXTRA.includes(extension) ||
    TEXT_EXTENSIONS.includes(extension)
  ) {
    return FileText;
  }

  // Presentation files
  if (
    PRESENTATION_EXTENSIONS.includes(extension) ||
    PRESENTATION_EXTENSIONS_EXTRA.includes(extension)
  ) {
    return Presentation;
  }

  // Image files
  if (IMAGE_EXTENSIONS.includes(extension)) {
    return Image;
  }

  // Video files
  if (VIDEO_EXTENSIONS.includes(extension)) {
    return FileVideo;
  }

  // Audio files
  if (AUDIO_EXTENSIONS.includes(extension)) {
    return FileAudio;
  }

  // Default for other files (code, pdf, etc.)
  return FileCode2;
};

/**
 * Gets the background color for a file icon based on file extension.
 * @param filename - The filename with extension
 * @returns The hex color code for the file type
 */
export const getFileIconBackgroundColor = (filename: string): string => {
  const extension = filename.split('.').pop()?.toLowerCase() || '';

  // Spreadsheet files
  if (SPREADSHEET_EXTENSIONS.includes(extension)) {
    return FILE_TYPE_COLORS.spreadsheet;
  }

  // Word/text files
  if (
    DOCUMENT_EXTENSIONS.includes(extension) ||
    DOCUMENT_EXTENSIONS_EXTRA.includes(extension) ||
    TEXT_EXTENSIONS.includes(extension)
  ) {
    return FILE_TYPE_COLORS.document;
  }

  // Presentation files
  if (
    PRESENTATION_EXTENSIONS.includes(extension) ||
    PRESENTATION_EXTENSIONS_EXTRA.includes(extension)
  ) {
    return FILE_TYPE_COLORS.presentation;
  }

  // Image files
  if (IMAGE_EXTENSIONS.includes(extension)) {
    return FILE_TYPE_COLORS.image;
  }

  // Video files
  if (VIDEO_EXTENSIONS.includes(extension)) {
    return FILE_TYPE_COLORS.video;
  }

  // Audio files
  if (AUDIO_EXTENSIONS.includes(extension)) {
    return FILE_TYPE_COLORS.audio;
  }

  // Default for other files (code, pdf, etc.)
  return FILE_TYPE_COLORS.default;
};
